"use client";

import {
  convertLocationToSuggestion,
  Currencies,
  ListingCardData,
  ListingTypeEnum,
  LocationData,
  Search,
} from "@/types/types";
import styles from "./page.module.css";
import MapWrapperClient from "./components/map/map-wrapper.client";
import { Figtree } from "next/font/google";
import Footer from "./components/footer/footer";
import { useTranslations } from "next-intl";
// import { getFilteredListingCountFromLocationData } from "@/lib/getFilteredLocationsData/location_data";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SortingPillClient from "./components/sorting-pill.client";
import { FaFilter, FaMap } from "react-icons/fa6";
import {
  MdClose,
  MdOpenInFull,
  MdOutlineCloseFullscreen,
} from "react-icons/md";
import { DeviceType, useDeviceType } from "@/hooks/use-device-type";
// import ListingMobileDialog from "./components/listing-mobile-dialog";
import CardsListClient from "./components/cards-list/cards-list.client";
import { FetchListingsResponse, MapClusterType, MapPin } from "./types";
import MoreFiltersComponent from "./components/more-filters/more-filters-component";
import MobileSearchbarClient from "./components/mobile-searchbar.client";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { getCurrency } from "@/lib/currency-data/currency";
import { buildSearchParamsForUrlUpdate } from "@/lib/buildSearchParams/buildSearchParams";
import { usePathname } from "next/navigation";
import CardPreview from "./components/listing-cards/card-preview";
import {
  getRouteKeyFromSlug,
  makeLocalizedHref,
} from "@/lib/localizedRoutes/routeHelpers";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePageStore } from "./usePageStore";
import MapWrapperServer from "./components/map/map-wrapper.server";
import PageSearchbar from "./components/page-searchbar/page-searchbar";
import SearchBreadcrumbsClient from "@/components/breadcrumbs/breadcrumbs";
import Breadcrumbs from "@/components/breadcrumbs/breadcrumbs";
import { toast } from "react-toastify";
import ListingCardPremium from "./components/listing-cards/listing-card-premium";
import ListingCard from "./components/listing-cards/listing-card";
import ListingCardLoading from "./components/listing-cards/listing-card-loading";
import { getLocalizedMapSlug } from "./localizedMapSlugs";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

const figtree = Figtree({ weight: ["400", "500", "600"], subsets: ["latin"] });

export default function SearchResultElementsClient({
  search,
  listingsResponse,
  mapPoints,
  mapClusters,
  locationData,
  locale,
  userSub,
  userLat,
  userLon,
  numberOfActiveFilters,
  initialMapError,
}: {
  search: Search;
  listingsResponse: FetchListingsResponse;
  mapPoints: MapPin[];
  mapClusters: MapClusterType[];
  locationData: LocationData;
  locale: string;
  userSub: string | null;
  userLat: string | null;
  userLon: string | null;
  numberOfActiveFilters: number;
  initialMapError: boolean;
}) {
  const initialLocationName: string = `${
    locationData.names[locale] ?? locationData.names["en"] ?? ""
  }`;
  const [isMobileMapOpen, setIsMobileMapOpen] = useState(false);
  const allowMobileMap = useIsMobile(900);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const storeInitialized = useRef(false);
  const t = useTranslations("SearchResult");
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const filtersParentRef = useRef<HTMLDivElement>(null);
  const deviceType = useDeviceType();
  const isLoaded = usePageStore((state) => state.isLoaded);
  const locationName = usePageStore((state) => state.locationName);
  const total_count_listings = usePageStore(
    (state) => state.ListingCardsState.total_count,
  );
  const setDeviceType = usePageStore((state) => state.setDeviceType);
  const initSearch = usePageStore((state) => state.initSearch);
  const initMapState = usePageStore((state) => state.initMapState);
  const initCardsListState = usePageStore((state) => state.initCardsListState);
  const setInitialSuggestion = usePageStore(
    (state) => state.setInitialSuggestion,
  );
  const currentSuggestion = convertLocationToSuggestion(locationData);
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  const { userData } = useUserAuthDataContext();
  const userCurrency = getCurrency(userData.currency);
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `${userCurrency.Name}-EUR` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const reverseCurrencyKey = `EUR-${userCurrency.Name}` as keyof Currencies;
  const reverseRate: number = currenciesRates[reverseCurrencyKey];
  const isImperial = userData.unit_system === "Imperial";
  const pathname = usePathname();
  const storedSearch = usePageStore((state) => state.search);
  const filtersTrigger = usePageStore((state) => state.filtersTrigger);
  const prevPageRef = useRef(storedSearch.page_number);
  const lastUrlRef = useRef<string | null>(null);
  const hasSyncedRef = useRef(false);
  const oldCurrency = useRef(userData.currency);
  const breadcrumbs = usePageStore(
    (state) => state.ListingCardsState.breadcrumbs,
  );
  const isFullMapView = usePageStore((state) => state.isFullMapView);
  const setIsFullMapView = usePageStore((state) => state.setIsFullMapView);
  const activeMapListing = usePageStore((state) => state.activeMapListing);
  const activeMapPin = usePageStore((state) => state.activeMapPin);
  const setActiveMapPin = usePageStore((state) => state.setActiveMapPin);
  const isActiveMapListingLoading = usePageStore(
    (state) => state.isActiveMapListingLoading,
  );
  const triggerCameraRestore = usePageStore(
    (state) => state.triggerCameraRestore,
  );
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollY = useRef(0);
  const hasTarget = !!(
    storedSearch.target_agency_id || storedSearch.target_user_id
  );
  const targetName =
    hasTarget && breadcrumbs.length > 0 ? breadcrumbs[0].name : "";
  const ssrHasTarget = !!(search.target_agency_id || search.target_user_id);
  //in case on user preferences changes do not happen is because of this here...
  //it will need a specific update, right now we will rely on this rerun for recalculation, may God be with us
  if (!storeInitialized.current) {
    // We grab the actions directly from the store's current state to update it synchronously
    const pageStore = usePageStore.getState();
    const cacheStore = useListingsCacheStore.getState();

    pageStore.setInitialSuggestion(currentSuggestion, locale);
    pageStore.initMapState(
      search,
      mapPoints,
      mapClusters,
      cacheStore.setMultipleListings,
      locationData.inverted_boundary_geojson,
    );
    pageStore.initSearch(search, userSub, rate, isImperial, reverseRate);
    pageStore.initCardsListState(
      listingsResponse,
      cacheStore.setMultipleListings,
    );

    // Force the loaded state to true immediately
    usePageStore.setState({ isLoaded: true });

    storeInitialized.current = true;
  }

  const hasToastedRef = useRef(false);

  useEffect(() => {
    if (initialMapError && !hasToastedRef.current) {
      toast("Unable to get map listings.", {
        type: "error",
      });
      hasToastedRef.current = true;
    }
  }, [initialMapError]);

  //update page number
  useEffect(() => {
    const slugArray = pathname.split("/").filter(Boolean);
    const currentRouteKey = getRouteKeyFromSlug(locale, slugArray[0]);

    if (currentRouteKey === "listing") return;
    if (storedSearch.purpose === "any" || storedSearch.type === "any") return;
    if (!hasSyncedRef.current) {
      if (storedSearch.location_url === search.location_url) {
        hasSyncedRef.current = true;
      } else {
        return;
      }
    }

    if (userData.currency !== oldCurrency.current) {
      // Skip updating URL on currency change
      oldCurrency.current = userData.currency;
      return;
    }
    const urlLocationSegment =
      storedSearch.location_url === "map"
        ? getLocalizedMapSlug(locale)
        : storedSearch.location_url;
    const newPathname = makeLocalizedHref(locale, "search", [
      urlLocationSegment,
      storedSearch.type,
      storedSearch.purpose,
      storedSearch.page_number.toString(),
    ]);
    const newQuery = buildSearchParamsForUrlUpdate(
      storedSearch,
      locale,
      isImperial,
      rate,
    );
    const params = new URLSearchParams(newQuery).toString();
    const newUrl = `${newPathname}?${params}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (newUrl === currentUrl || newUrl === lastUrlRef.current) {
      return;
    }

    if (newUrl !== currentUrl) {
      window.history.replaceState(null, "", newUrl);
    }

    lastUrlRef.current = newUrl;
  }, [storedSearch, locale]);

  const scrollToCardsTop = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = 0;
    // containerRef.current.scrollTop = 0;

    // const rect = containerRef.current.getBoundingClientRect();
    // const absoluteTop = rect.top + window.scrollY;

    // window.scrollTo({
    //   top: absoluteTop,
    //   behavior: "auto",
    // });
  };

  useEffect(() => {
    if (!allowMobileMap && isMobileMapOpen) {
      setIsMobileMapOpen(false);
    }
  }, [allowMobileMap]);

  useEffect(() => {
    if (storedSearch.page_number !== prevPageRef.current) {
      scrollToCardsTop(); // page actually changed
      prevPageRef.current = storedSearch.page_number;
    }
  }, [storedSearch.page_number]);

  useEffect(() => {
    scrollToCardsTop(); // page actually changed
  }, [filtersTrigger]);

  useEffect(() => {
    scrollToCardsTop(); // page actually changed
  }, [storedSearch.location_url]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let ticking = false;

    const emitScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = container.scrollTop;

          if (scrollY > lastScrollY.current + 10) {
            setIsScrollingDown(true);
          } else if (scrollY < lastScrollY.current - 10) {
            setIsScrollingDown(false);
          }
          lastScrollY.current = scrollY;

          const event = new CustomEvent("custom-container-scroll", {
            detail: { scrollY },
          });
          window.dispatchEvent(event);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", emitScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", emitScroll);
    };
  }, [isFullMapView]);

  useEffect(() => {
    setDeviceType(deviceType);
  }, [deviceType]);

  const validateSeachFilters = usePageStore(
    (state) => state.validateSeachFilters,
  );
  const handleCloseFilters = useCallback(() => {
    if (validateSeachFilters()) return;
    setIsMobileFiltersOpen(false);
  }, [validateSeachFilters]);

  useDialogRouting(
    "MobileFilters",
    deviceType === DeviceType.mobile && isMobileFiltersOpen,
    handleCloseFilters,
  );

  const closeMobileMap = useCallback(() => setIsMobileMapOpen(false), []);

  useDialogRouting(
    "MobileMap",
    deviceType === DeviceType.mobile && isMobileMapOpen,
    closeMobileMap,
  );
  const prevAllowMobileMap = useRef(allowMobileMap);
  useEffect(() => {
    // 🛑 ONLY run this if the screen size actually transitioned from Desktop to Mobile
    const justSwitchedToMobile = allowMobileMap && !prevAllowMobileMap.current;

    if (justSwitchedToMobile) {
      // 1. Force-close the Desktop Full Map view
      if (isFullMapView) {
        setIsFullMapView(false);
      }

      // 2. Clear the active listing and restore camera if a card was open
      if (activeMapPin) {
        setActiveMapPin(null, locale);
        triggerCameraRestore();
      }
    }

    // Always update the ref so we know the state for the next render
    prevAllowMobileMap.current = allowMobileMap;
  }, [
    allowMobileMap,
    isFullMapView,
    activeMapPin,
    setIsFullMapView,
    setActiveMapPin,
    triggerCameraRestore,
    locale,
  ]);
  return (
    <main className={`${figtree.className} ${styles.main}`}>
      <div className={styles.searchbarWrapper}>
        <PageSearchbar
          ssrSearch={search}
          numberOfActiveFilters={numberOfActiveFilters}
          locale={locale}
          userLat={userLat}
          userLon={userLon}
        />
      </div>
      <div className={styles.mobileSearchbarWrapper}>
        <MobileSearchbarClient lat={userLat} long={userLon} />
      </div>
      <div
        className={`${styles.contentWrapper} ${isFullMapView ? styles.fullMapActive : ""}`}
      >
        <div
          className={`${styles.mapWrapper} ${
            !isMobileMapOpen && styles.hidden
          }`}
        >
          {!allowMobileMap && isLoaded && (
            <button
              className={styles.fullScreenToggleBtn}
              onClick={(e) => {
                e.preventDefault();
                setIsFullMapView(!isFullMapView);
              }}
            >
              {isFullMapView ? (
                <MdOutlineCloseFullscreen size={18} />
              ) : (
                <MdOpenInFull size={18} />
              )}
              <span>{isFullMapView ? t("exitMap") : t("fullMap")}</span>
            </button>
          )}

          <button
            className={`${styles.closeMapButton} ${!isMobileMapOpen && styles.hidden}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeMobileMap();
            }}
          >
            <MdClose />
          </button>
          <Suspense fallback={<MapWrapperServer />}>
            <MapWrapperClient isMapOpen={isMobileMapOpen} />
          </Suspense>
        </div>
        {!isFullMapView && (
          <div className={styles.cardsWrapper} ref={containerRef}>
            <div className={styles.headingWrapper}>
              <div className={styles.titleContainer}>
                {isLoaded ? (
                  <Breadcrumbs
                    locale={locale}
                    breadcrumbs={breadcrumbs}
                    targetAgencyId={storedSearch.target_agency_id}
                    targetUserId={storedSearch.target_user_id}
                  />
                ) : (
                  <div className={styles.breadcrumbsLoading} />
                )}
                <h1 className={styles.title}>
                  <span>
                    {isLoaded ? (
                      <>
                        {t(
                          storedSearch.type !== "any"
                            ? storedSearch.type
                            : search.type,
                        )}{" "}
                        {t(
                          storedSearch.purpose !== "any"
                            ? storedSearch.purpose
                            : search.purpose,
                        )}{" "}
                        {hasTarget && targetName && (
                          <>
                            {t("by")} {targetName}{" "}
                          </>
                        )}
                        {storedSearch.location_url === "map" ? (
                          t("onMap")
                        ) : (
                          <>
                            {t("in")}{" "}
                            {locationName !== ""
                              ? locationName
                              : initialLocationName}
                          </>
                        )}{" "}
                        ({total_count_listings})
                      </>
                    ) : !ssrHasTarget &&
                      search.listing_count !== undefined &&
                      search.listing_count !== null &&
                      (search.location_name ||
                        search.location_url === "map") ? (
                      <span>
                        {t(search.type)} {t(search.purpose)}{" "}
                        {search.location_url === "map" ? (
                          t("onMap")
                        ) : (
                          <>
                            {t("in")} {search.location_name}
                          </>
                        )}{" "}
                        ({search.listing_count})
                      </span>
                    ) : (
                      <span className={styles.titleLoading} />
                    )}
                  </span>
                </h1>
              </div>
              <SortingPillClient locale={locale} />
            </div>
            <CardsListClient locale={locale} />
            <div className={`${styles.floatingButtonsWrapper}`}>
              <button
                className={styles.mapButton}
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMapOpen(!isMobileMapOpen);
                }}
              >
                <span className={styles.icon}>
                  <FaMap />
                </span>
                <span className={styles.text}>{t("map")}</span>
              </button>
              <div className={styles.separator}></div>
              <button
                className={styles.filtersButton}
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileFiltersOpen(!isMobileFiltersOpen);
                }}
              >
                <span className={styles.icon}>
                  <FaFilter />
                </span>
                <span className={styles.text}>{t("filters")}</span>
              </button>
            </div>
            {isMobileFiltersOpen && (
              <div
                className={styles.filtersComponentWrapper}
                ref={filtersParentRef}
              >
                <button
                  className={styles.closeButton}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCloseFilters();
                  }}
                >
                  <MdClose />
                </button>
                <MoreFiltersComponent
                  onClose={handleCloseFilters}
                  parentRef={filtersParentRef}
                />
              </div>
            )}
            <div ref={footerRef} className={styles.footerWrapper}>
              <Footer />
            </div>
          </div>
        )}
        {isFullMapView && activeMapListing && (
          <div className={styles.floatingInspectorWrapper}>
            <div className={styles.floatingInspectorInner}>
              <button
                className={styles.inspectorCloseBtn}
                onClick={() => {
                  setActiveMapPin(null, locale);
                  triggerCameraRestore(); // ✈️ Fly back to the macro view!
                }}
              >
                <MdClose size={20} />
              </button>

              <div className={styles.cardContainer}>
                {isActiveMapListingLoading || !activeMapListing ? (
                  <div className={styles.listingLoadingWrapper}>
                    <ListingCardLoading activePlan={"prime"} />
                  </div>
                ) : activeMapListing.listing_type === ListingTypeEnum.PRIME ||
                  activeMapListing.listing_type ===
                    ListingTypeEnum.SPOTLIGHT ? (
                  <ListingCardPremium
                    data={activeMapListing}
                    isResizable={true}
                    hasActions={false}
                    hasRoundButtons={true}
                  />
                ) : (
                  <ListingCard
                    data={activeMapListing}
                    isResizable={false}
                    hasActions={false}
                    hasRoundButtons={true}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <CardPreview />
      {/* <ListingMobileDialog /> */}
    </main>
  );
}

// function updatePageInPath(pathname: string, page: number): string {
//   const parts = pathname.split("/");
//   // Replace last segment with page number
//   parts[parts.length - 1] = String(page);
//   return parts.join("/");
// }
